import { useDispatch } from 'react-redux';
import type { Asset, InventoryItem } from 'core/types';
import { usePhotoGallery } from 'core/hooks/usePhotoGallery';
import {
  getFirebaseDownloadUrl,
  useFirebaseUpload
} from 'core/hooks/useFirebase';
import { useAnalyzeProductImage } from 'core/hooks/useAnalyzeProductImage';
import { displayError, displaySuccess } from 'core/services/AlertActions';
import { saveProductUpdate } from 'features/shared/inventory/utils/saveProductUpdate';
import { resolveAnalyzedProductUpdate } from 'features/shared/inventory/utils/resolveAnalyzedProductUpdate';

/**
 * Add-a-photo and analyze-a-photo actions for an existing product's item
 * detail (desktop + mobile), pulled out of InventoryDetailsDesktop.tsx and
 * InventoryDetailsMobile.tsx's byte-identical copies. `onAnalyzed` lets each
 * caller own how the analyze modal itself closes (both currently just
 * `setIsAnalyzeModalOpen(false)`).
 */
export const useProductPhotoActions = (
  homeId: number | undefined,
  product: InventoryItem | null | undefined,
  onAnalyzed: () => void
) => {
  const dispatch = useDispatch();
  const { takePhoto } = usePhotoGallery();
  const { uploadFile } = useFirebaseUpload();
  const { analyze, isAnalyzing } = useAnalyzeProductImage();

  // "Add Image" (usePhotoGallery/Capacitor Camera → Firebase Storage), then
  // PATCHes the hosted URL as `model_photo` — the API's _manage_photo() turns
  // that into a new Assets row appended to this product's `assets`, so it
  // shows up in the image slider on the refreshed product returned by the
  // PATCH response.
  const addImage = () => {
    if (!homeId || !product) return;
    takePhoto()
      .then((photo) => {
        if (!photo.filePath || !photo.imageData) return;
        const fileName = `product_${product.id}_${new Date().getTime()}.jpg`;
        uploadFile(homeId, fileName, photo.imageData)
          .then((upload) => getFirebaseDownloadUrl(upload.metadata.fullPath))
          .then((url) =>
            saveProductUpdate(dispatch, homeId, product.id, {
              model_photo: url
            })
          )
          .then(({ ok }) => {
            // Only claim the image was added once the PATCH that attaches it
            // actually succeeded — createApiRequest resolves either way.
            if (ok) {
              dispatch(displaySuccess('Image Added', 'Success'));
            }
          })
          .catch((error) => {
            dispatch(
              displayError(error?.message || 'Unable to add this photo.')
            );
          });
      })
      .catch((error) => {
        // Capacitor's Camera plugin (web and native) rejects with this exact
        // message when the user backs out of the picker — that's not a
        // failure worth surfacing. Anything else (permission denial, camera
        // hardware error, gallery read failure) should reach the user.
        if (error?.message === 'User cancelled photos app') return;
        dispatch(displayError(error?.message || 'Unable to add this photo.'));
      });
  };

  /**
   * Analyzes a homeowner-chosen photo with avid-warranty-cloud's OCR endpoint
   * (useAnalyzeProductImage, same hook InventoryAddProduct.tsx uses for the
   * Add flow). Serial # applies directly; Brand/Manufacturer and Model # are
   * resolved against the catalog search endpoints first (see
   * resolveAnalyzedProductUpdate) since there's nowhere to store free text for
   * either — only an exact name match applies, anything else is surfaced in
   * the toast instead of silently guessed.
   */
  const analyzeImage = async (photo: Asset) => {
    if (!homeId || !product) return;
    try {
      const result = await analyze(photo.file_url);
      const { body, appliedLabels, unmatchedNotes } =
        await resolveAnalyzedProductUpdate(
          dispatch,
          homeId,
          product.product_template.type_id,
          result
        );
      const { ok } = await saveProductUpdate(
        dispatch,
        homeId,
        product.id,
        body
      );
      // Don't report what the photo "updated" if the PATCH itself failed —
      // createApiRequest already surfaced the API error.
      if (!ok) return;
      const unmatchedNote = unmatchedNotes.length
        ? ` Couldn't match ${unmatchedNotes.join(
            ' or '
          )} to the catalog — update manually if needed.`
        : '';
      dispatch(
        displaySuccess(
          appliedLabels.length
            ? `Updated ${appliedLabels.join(
                ', '
              )} from the photo.${unmatchedNote}`
            : `No matching details were found on this photo.${unmatchedNote}`,
          'Analyzed'
        )
      );
      onAnalyzed();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : undefined;
      dispatch(
        displayError(message || 'Unable to analyze this photo right now.')
      );
    }
  };

  return { addImage, analyzeImage, isAnalyzing };
};
